import { useEffect, useState } from "react";

import { AIAgentSessionHelper } from "@/helpers/agentcore/ai-agent-session-helper";

import type { AgentResponse } from "../@types/agentcore";

/** What the agent is doing, as the surfaces that report it need to read it. */
export interface AgentProgress {
  /** The agent's latest message, or undefined until it has sent one. */
  readonly message: string | undefined;
  /** Whether the agent has handed back and is waiting on the hire. */
  readonly isUserTurn: boolean;
  /** Whether a turn is in flight, which is what the typing indicator reports. */
  readonly isProcessing: boolean;
  /**
   * Where the hire signs in, as the agent reported it on the turn it handed back.
   *
   * Kept apart from `message` because that cannot do this job: it is English only, and the user-turn
   * card replaces it with translated steps. Undefined until the agent names a flow, and cleared
   * whenever the turn is no longer the hire's, so a second run cannot inherit the first one's answer.
   */
  readonly authLocation: AgentResponse["auth_location"];
}

/**
 * The agent's progress, for a surface that reports it.
 *
 * Each caller subscribes for itself: the banner and the step's indicator are mounted apart, and the
 * host's listener is additive, which two callers here already rely on.
 *
 * @param isListening False stops listening and reports nothing in flight.
 */
export const useAgentProgress = (isListening: boolean): AgentProgress => {
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [isUserTurn, setIsUserTurn] = useState(false);
  const [isProcessing, setIsProcessing] = useState(isListening);
  const [authLocation, setAuthLocation] =
    useState<AgentResponse["auth_location"]>();

  useEffect(() => {
    if (!isListening) {
      /*
       * The message goes too. A long-lived caller stays mounted between runs, and keeping the last
       * one would open the next run on the previous run's status line.
       */
      setMessage(undefined);
      setIsUserTurn(false);
      setIsProcessing(false);
      setAuthLocation(undefined);
      return;
    }
    setIsProcessing(true);

    const cleanup = window.electronAPI?.onAgentActionProgress?.((result) => {
      if (!result.success || !result.response) {
        return;
      }
      const { message: nextMessage, state } = result.response;
      if (nextMessage) {
        setMessage(nextMessage);
      }
      const isHandedBack = state === "PENDING_USER_ACTION";
      setIsUserTurn(isHandedBack);
      /* One list of terminal states, in the helper, so this cannot disagree with it. */
      setIsProcessing(
        !isHandedBack && !AIAgentSessionHelper.isTerminalState(result.response)
      );
      /*
       * Only a hand-off carries a flow. Every other turn clears it, so the agent taking its turn back
       * and handing over again without naming one cannot leave the last answer on screen.
       */
      setAuthLocation(isHandedBack ? result.response.auth_location : undefined);
    });

    return () => cleanup?.();
  }, [isListening]);

  return { authLocation, isProcessing, isUserTurn, message };
};
